import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { BRAIN_DESCRIPTION } from "../copy/companyBrainCopy";
import { BRAIN_ADD_CATEGORIES, FUTURE_SOURCES, brainCategoryLabel, brainState, groupBrainSources } from "./companyBrain";
import { CODEBASE_GUIDE_PRIORITIES, guidePriorityLabel } from "../codebaseGuideConstants";
import InlineAlert from "../ui/InlineAlert";
import Skeleton from "../ui/Skeleton";
import "./GuidesPage.css";

export const GUIDE_CUSTOMER_CATEGORIES = BRAIN_ADD_CATEGORIES;

export function guideCustomerCategoryLabel(value) {
  return brainCategoryLabel(value);
}

function provenanceLabel(guide) {
  const origin = guide?.provenance?.origin;
  if (origin === "manual") return guide?.approvedBy ? "Human approved" : "Human authored";
  if (origin === "legacy_codebase_guide") return "Imported from Codebase Guide";
  if (!origin || origin === "unknown") return "Provenance was not recorded";
  return "AI suggested, then human approved";
}

function statusLabel(guide) {
  if (guide?.status) return guide.status.replace(/_/g, " ");
  return guide?.isActive === false ? "inactive" : "active";
}

function GuideStatusBadge({ guide }) {
  return <span className={`guide-status guide-status-${guide?.status || (guide?.isActive === false ? "inactive" : "active")}`}>{statusLabel(guide)}</span>;
}

export default function GuidesPage({
  repositories = [],
  section = "guides",
  guideId = "",
  suggestionId = "",
  conflictId = "",
  repositoryId = "",
  filters = {},
  requestedLens = "",
  mutateAllowed = false,
  adminAllowed = false,
  prefill = null,
  onOpen,
  onCreated,
}) {
  const [guides, setGuides] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [confirming, setConfirming] = useState("");
  const [draft, setDraft] = useState({
    title: prefill?.title || "",
    body: prefill?.body || "",
    category: prefill?.category || "product_context",
    priority: "medium",
    pathScope: prefill?.pathScope || "",
    rationale: prefill?.rationale || "",
    repoId: repositoryId || repositories[0]?._id || "",
    categoryLabel: "Product promises",
  });
  const [extraCategories, setExtraCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [edit, setEdit] = useState(null);
  const [suggestionEdit, setSuggestionEdit] = useState(null);
  const [lens, setLens] = useState(requestedLens === "attention" ? "attention" : "rules");
  const choseInitialLens = useRef(false);
  const experimental = process.env.REACT_APP_EXPERIMENTAL_TASKS === "true";

  const repoId = repositoryId || draft.repoId || (repositories.length === 1 ? String(repositories[0]._id) : "");
  const selectedRepo = repositories.find((repo) => String(repo._id) === String(repoId));

  useEffect(() => {
    if (requestedLens === "attention" || requestedLens === "proposals" || requestedLens === "sources" || requestedLens === "rules") {
      setLens(requestedLens);
    }
  }, [requestedLens]);

  useEffect(() => {
    if (requestedLens || choseInitialLens.current || (!suggestions.length && !conflicts.length)) return;
    choseInitialLens.current = true;
    const needsAttention = conflicts.length || suggestions.some((item) =>
      item.status === "outdated" || (item.status === "pending" && item.safeToUse === false)
    );
    if (needsAttention) setLens("attention");
  }, [conflicts, suggestions, requestedLens]);

  useEffect(() => {
    if (!repoId || guideId === "new") {
      setGuides([]);
      return undefined;
    }
    let cancelled = false;
    setError("");
    api
      .listGuides(repoId, {
        category: filters.category || undefined,
        priority: filters.priority || undefined,
        status: filters.status || undefined,
        scope: filters.scope || undefined,
        q: filters.q || undefined,
      })
      .then((response) => {
        if (cancelled) return;
        const guides = response.data?.guides || response.data?.entries || [];
        setGuides(guides);
      })
      .catch(() => api.listCodebaseGuide(repoId, { activeOnly: true }).then((response) => {
        if (cancelled) return;
        setGuides(response.data?.entries || []);
      }).catch(() => {
        if (!cancelled) setError("Guides could not be loaded.");
      }));
    api.listGuideSuggestions(repoId).then((response) => {
      if (!cancelled) setSuggestions(response.data?.suggestions || []);
    }).catch(() => {
      if (!cancelled) setSuggestions([]);
    });
    api.listGuideConflicts(repoId).then((response) => {
      if (!cancelled) setConflicts(response.data?.conflicts || []);
    }).catch(() => {
      if (!cancelled) setConflicts([]);
    });
    return () => {
      cancelled = true;
    };
  }, [repoId, guideId, filters.category, filters.priority, filters.status, filters.scope, filters.q]);

  useEffect(() => {
    if (section !== "guide-history" || !repoId || !guideId) return undefined;
    let cancelled = false;
    api.guideHistory(repoId, guideId).then((response) => {
      if (!cancelled) setHistory(response.data?.revisions || []);
    }).catch(() => {
      if (!cancelled) setError("Guide history could not be loaded.");
    });
    return () => {
      cancelled = true;
    };
  }, [section, repoId, guideId]);

  const selected = useMemo(() => (guides || []).find((entry) => String(entry._id) === String(guideId)), [guides, guideId]);
  const selectedSuggestion = useMemo(
    () => suggestions.find((item) => String(item._id) === String(suggestionId)) || suggestions[0],
    [suggestions, suggestionId]
  );
  const selectedConflict = useMemo(
    () => conflicts.find((item) => String(item._id) === String(conflictId)) || conflicts[0],
    [conflicts, conflictId]
  );

  function addCategory() {
    const label = newCategory.trim();
    if (!label) return;
    const known = GUIDE_CUSTOMER_CATEGORIES.find((item) => item.label.toLowerCase() === label.toLowerCase());
    const custom = extraCategories.find((item) => item.label.toLowerCase() === label.toLowerCase());
    const next = known || custom || { value: "other", label };
    if (!known && !custom) setExtraCategories((current) => [...current, next]);
    setDraft((current) => ({ ...current, category: next.value, categoryLabel: next.label }));
    setNewCategory("");
  }

  async function saveGuide(event) {
    event.preventDefault();
    setError("");
    const known = GUIDE_CUSTOMER_CATEGORIES.find((item) => item.value === draft.category && item.label === draft.categoryLabel);
    const payload = {
      title: draft.title.trim(),
      body: draft.body.trim(),
      category: known ? known.value : "other",
      customerCategory: known ? "" : draft.categoryLabel,
      priority: draft.priority,
      pathScope: [],
      rationale: draft.rationale,
      isActive: true,
    };
    if (!payload.title || !payload.body || !repoId) {
      setError("Choose a codebase above, then write what RenderRepo should remember.");
      return;
    }
    const response = await api.createGuide(repoId, payload);
    onCreated?.(response.data?.guide);
  }

  async function saveEdit(event) {
    event.preventDefault();
    if (!selected || !edit) return;
    const response = await api.updateGuide(repoId, selected._id, {
      title: edit.title,
      body: edit.body,
      rationale: edit.rationale,
      pathScope: selected.pathScope || [],
    });
    setGuides((current) => (current || []).map((item) => (item._id === selected._id ? response.data.guide : item)));
    setEdit(null);
    setNotice("Saved as a new revision. Earlier revisions stay unchanged.");
  }

  async function approveSuggestion(event) {
    event.preventDefault();
    if (!selectedSuggestion) return;
    const response = await api.acceptGuideSuggestion(repoId, selectedSuggestion._id, {
      scopeConfirmed: true,
      title: suggestionEdit?.title || selectedSuggestion.title,
      body: suggestionEdit?.body || selectedSuggestion.body,
      pathScope: (suggestionEdit?.pathScope || selectedSuggestion.pathScope || []).join
        ? suggestionEdit?.pathScope || selectedSuggestion.pathScope
        : String(suggestionEdit?.pathScope || "").split(",").map((item) => item.trim()).filter(Boolean),
    });
    setNotice("Approved. The suggestion stayed in history and did not become active before this action.");
    if (response.data?.guide?._id) onCreated?.(response.data.guide);
  }

  async function resolve(resolution, scopes = {}) {
    if (!selectedConflict) return;
    if (confirming !== resolution) {
      setConfirming(resolution);
      return;
    }
    await api.resolveGuideConflict(repoId, selectedConflict._id, { resolution, ...scopes });
    setConfirming("");
    setNotice("Conflict resolved. Neither side was replaced without this choice.");
    onOpen?.({ repositoryId: repoId, section: "conflicts" });
  }

  const nav = (
    <nav className="guide-sections" aria-label="Guide sections">
      <button type="button" aria-current={section === "guides" || section === "guide" ? "page" : undefined} onClick={() => onOpen?.({ repositoryId: repoId })}>Guides</button>
      <button type="button" aria-current={String(section).includes("suggestion") ? "page" : undefined} onClick={() => onOpen?.({ repositoryId: repoId, section: "suggestions" })}>Suggestions</button>
      <button type="button" aria-current={String(section).includes("conflict") ? "page" : undefined} onClick={() => onOpen?.({ repositoryId: repoId, section: "conflicts" })}>Conflicts</button>
      <button type="button" disabled={!selected && section !== "guide-history"} onClick={() => selected && onOpen?.({ guideId: selected._id, repositoryId: repoId, section: "history" })}>History</button>
    </nav>
  );

  if (guideId === "new") {
    return (
      <form className="guide-page" onSubmit={saveGuide}>
        <header className="guide-header">
          <p className="rr-kicker">Company Brain</p>
          <h1>Add context</h1>
          <p>What should RenderRepo remember? This is saved as a confirmed rule.</p>
        </header>
        {error ? <InlineAlert tone="alert">{error}</InlineAlert> : null}
        <p className="guide-codebase">{selectedRepo ? `Saved to ${selectedRepo.owner}/${selectedRepo.name}.` : "Choose a codebase above before adding context."}</p>
        <label>What should RenderRepo remember?
          <textarea value={draft.body} onChange={(event) => setDraft({ ...draft, body: event.target.value, title: event.target.value.slice(0, 80) })} rows={3} placeholder="Only workspace owners can change billing settings." />
        </label>
        <label>Why it matters<textarea value={draft.rationale} onChange={(event) => setDraft({ ...draft, rationale: event.target.value })} rows={3} /></label>
        <fieldset className="guide-categories">
          <legend>Category</legend>
          <div role="group" aria-label="Category">
            {[...GUIDE_CUSTOMER_CATEGORIES, ...extraCategories].map((item) => (
              <button
                key={item.value}
                type="button"
                className="rr-chip"
                aria-pressed={draft.category === item.value && draft.categoryLabel === item.label}
                onClick={() => setDraft({ ...draft, category: item.value, categoryLabel: item.label })}
              >
                {item.label}
              </button>
            ))}
          </div>
          <div className="guide-new-category">
            <label>Add a category
              <input
                value={newCategory}
                onChange={(event) => setNewCategory(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    addCategory();
                  }
                }}
                placeholder="Billing rules"
              />
            </label>
            <button type="button" className="rr-button secondary" onClick={addCategory}>Add</button>
          </div>
        </fieldset>
        <label>Priority
          <select value={draft.priority} onChange={(event) => setDraft({ ...draft, priority: event.target.value })}>
            {CODEBASE_GUIDE_PRIORITIES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
        </label>
        <div className="rr-actions">
          <button type="submit" className="rr-button" disabled={!mutateAllowed}>Add to Company Brain</button>
        </div>
      </form>
    );
  }

  if (!guides) return <Skeleton label="Loading guides" />;

  if (section === "guide-suggestions" || section === "guide-suggestion") {
    return (
      <div className="guide-page">
        {nav}
        <header className="guide-header">
          <p className="rr-kicker">Suggestions</p>
          <h1>Needs review</h1>
          <p>These proposals are not active Guides. Approving one is a human action.</p>
        </header>
        {notice ? <InlineAlert>{notice}</InlineAlert> : null}
        {!suggestions.length ? <p className="guide-empty">No suggestions are waiting.</p> : null}
        {selectedSuggestion ? (
          <form className="guide-card suggestion-card" onSubmit={approveSuggestion}>
            <p className="guide-kicker">AI suggested</p>
            <h2>{selectedSuggestion.title}</h2>
            <p>{selectedSuggestion.body}</p>
            <p>{selectedSuggestion.rationale}</p>
            {selectedSuggestion.sourceExcerpt ? <blockquote>{selectedSuggestion.sourceExcerpt}</blockquote> : null}
            <p>Proposed scope: {(selectedSuggestion.pathScope || []).join(", ") || "Repository"}</p>
            <p>Source: {selectedSuggestion.origin}{selectedSuggestion.sourceSha ? ` · ${selectedSuggestion.sourceSha}` : ""}</p>
            {selectedSuggestion.sources?.length ? <p>{selectedSuggestion.sources.length} sources</p> : null}
            <label>Title<input defaultValue={selectedSuggestion.title} onChange={(event) => setSuggestionEdit({ ...(suggestionEdit || selectedSuggestion), title: event.target.value, pathScope: suggestionEdit?.pathScope || selectedSuggestion.pathScope })} /></label>
            <label>Rule<textarea defaultValue={selectedSuggestion.body} rows={4} onChange={(event) => setSuggestionEdit({ ...(suggestionEdit || selectedSuggestion), body: event.target.value })} /></label>
            <div className="rr-actions">
              <button type="submit" className="rr-button" disabled={!mutateAllowed}>Approve</button>
              <button type="button" className="rr-button secondary" disabled={!mutateAllowed} onClick={() => api.rejectGuideSuggestion(repoId, selectedSuggestion._id, { note: "Rejected" }).then(() => setNotice("Rejected. No Guide was created."))}>Reject</button>
            </div>
          </form>
        ) : null}
      </div>
    );
  }

  if (section === "guide-conflicts" || section === "guide-conflict") {
    return (
      <div className="guide-page">
        {nav}
        <header className="guide-header">
          <p className="rr-kicker">Conflicts</p>
          <h1>Resolve a disagreement</h1>
          <p>A conflict stays open until a person chooses. Similar wording alone does not create one.</p>
        </header>
        {notice ? <InlineAlert>{notice}</InlineAlert> : null}
        {!selectedConflict ? <p className="guide-empty">No open conflicts.</p> : (
          <article className="guide-card">
            <h2>{selectedConflict.summary}</h2>
            <p>Shared scope: {(selectedConflict.sharedScope || []).join(", ")}</p>
            <p>Confidence: {selectedConflict.confidence}. This is a scope and rule check, not a similarity score.</p>
            <div className="rr-actions">
              <button type="button" className="rr-button" disabled={!adminAllowed} onClick={() => resolve("keep_existing")}>{confirming === "keep_existing" ? "Confirm keep existing" : "Keep existing"}</button>
              <button type="button" className="rr-button secondary" disabled={!adminAllowed} onClick={() => resolve("separate_scopes", { leftPathScope: ["checkout/**"], rightPathScope: ["billing/**"] })}>{confirming === "separate_scopes" ? "Confirm separate scopes" : "Separate scopes"}</button>
              <button type="button" className="rr-button secondary" disabled={!adminAllowed} onClick={() => resolve("dismissed")}>{confirming === "dismissed" ? "Confirm dismiss" : "Dismiss"}</button>
            </div>
            {!adminAllowed ? <p>An owner or admin resolves conflicts.</p> : null}
          </article>
        )}
      </div>
    );
  }

  if (section === "guide-history" && selected) {
    return (
      <div className="guide-page">
        {nav}
        <header className="guide-header">
          <p className="rr-kicker">History</p>
          <h1>{selected.title}</h1>
        </header>
        <ol className="guide-history">
          {history.map((revision) => (
            <li key={revision.version}>
              <strong>Version {revision.version}</strong>
              <span>{revision.reason}</span>
              <p>{revision.title}</p>
              <p>{revision.body}</p>
              <p className="guide-meta">{revision.contentHash}</p>
            </li>
          ))}
        </ol>
        {!history.length ? <p>No stored revision yet. The next edit records one without erasing this text.</p> : null}
      </div>
    );
  }

  if (selected) {
    return (
      <article className="guide-page guide-detail">
        {nav}
        <header className="guide-header">
          <p className="rr-kicker">{selected.customerCategory || guideCustomerCategoryLabel(selected.category)}</p>
          <h1>{selected.title}</h1>
          <p className="guide-meta">
            <GuideStatusBadge guide={selected} />
            {" · "}
            {guidePriorityLabel(selected.priority)}
            {" · "}
            {provenanceLabel(selected)}
            {selected.lastVerifiedAt ? ` · Verified ${new Date(selected.lastVerifiedAt).toLocaleDateString()}` : " · Not yet verified"}
          </p>
        </header>
        {notice ? <InlineAlert>{notice}</InlineAlert> : null}
        {edit ? (
          <form onSubmit={saveEdit}>
            <label>Title<input value={edit.title} onChange={(event) => setEdit({ ...edit, title: event.target.value })} /></label>
            <label>Rule<textarea value={edit.body} onChange={(event) => setEdit({ ...edit, body: event.target.value })} rows={5} /></label>
            <label>Why<textarea value={edit.rationale || ""} onChange={(event) => setEdit({ ...edit, rationale: event.target.value })} rows={3} /></label>
            <button type="submit" className="rr-button">Save revision</button>
          </form>
        ) : (
          <>
            <h2>Rule</h2>
            <p className="guide-rule">{selected.body}</p>
            {selected.rationale ? <><h2>Why</h2><p>{selected.rationale}</p></> : null}
            <h2>Applies to</h2>
            <p>This codebase</p>
            <h2>Evidence</h2>
            <p>{selected.provenance?.sourceExcerpt || provenanceLabel(selected)}</p>
            <h2>Used by</h2>
            <p>{selected.usage?.tasks || 0} Tasks · {selected.usage?.reviews || 0} Reviews</p>
            {selected.quality?.warnings?.length ? <p className="guide-meta">Quality notes: {selected.quality.warnings.join(", ")}</p> : null}
          </>
        )}
        <div className="rr-actions">
          {experimental ? <Link className="rr-button" to={`/tasks/new?repository=${repoId}&guide=${selected._id}`}>Add to Task</Link> : null}
          {mutateAllowed ? <button type="button" className="rr-button secondary" onClick={() => setEdit({ title: selected.title, body: selected.body, rationale: selected.rationale || "", pathScope: (selected.pathScope || []).join(", ") })}>Edit</button> : null}
          {mutateAllowed ? <button type="button" className="rr-button secondary" onClick={() => api.verifyGuide(repoId, selected._id).then((response) => setNotice(`Verified ${response.data.guide.lastVerifiedAt || "now"}`))}>Verify</button> : null}
          {adminAllowed ? <button type="button" className="rr-button secondary" onClick={() => {
            if (confirming !== "deactivate") { setConfirming("deactivate"); return; }
            api.deactivateGuide(repoId, selected._id).then(() => onOpen?.({ repositoryId: repoId }));
          }}>{confirming === "deactivate" ? "Confirm deactivate" : "Deactivate"}</button> : null}
          <button type="button" className="rr-button secondary" onClick={() => onOpen?.({ guideId: selected._id, repositoryId: repoId, section: "history" })}>History</button>
        </div>
      </article>
    );
  }

  const confirmed = guides || [];
  const inferred = suggestions.filter((item) => item.status === "pending" && item.safeToUse !== false && !(item.conflictIds || []).length).slice(0, 12);
  const attention = [
    ...conflicts.slice(0, 8).map((item) => ({ id: item._id, state: "Conflicted", title: item.summary, section: "conflicts", conflictId: item._id })),
    ...suggestions.filter((item) => item.status === "outdated" || (item.dismissedFingerprints || []).length >= 2).slice(0, 8).map((item) => ({
      id: item._id,
      state: item.status === "outdated" ? "Outdated" : "Repeatedly dismissed",
      title: item.body || item.title,
      section: "suggestions",
      suggestionId: item._id,
    })),
    ...suggestions.filter((item) => item.status === "pending" && item.safeToUse === false && !(item.conflictIds || []).length).slice(0, 8).map((item) => ({
      id: item._id,
      state: "Needs confirmation",
      title: item.body || item.title,
      section: "suggestions",
      suggestionId: item._id,
    })),
  ];
  const repoName = repositories.find((repo) => String(repo._id) === String(repoId));
  const sourceGroups = groupBrainSources(suggestions);

  function ruleLine(item) {
    const text = String(item.body || item.title || "").trim();
    return text.length > 160 ? String(item.title || text).trim() : text;
  }

  function rowMeta(item) {
    const scope = item.customerCategory || brainCategoryLabel(item.category);
    const sources = item.sources?.length || (item.provenance ? 1 : 0);
    const verified = item.lastVerifiedAt || item.lastSeenAt;
    const when = verified ? `Verified ${new Date(verified).toLocaleDateString(undefined, { month: "long", day: "numeric" })}` : "Not yet verified";
    return `${brainState(item)} · ${scope} · ${sources || 1} sources · ${when}`;
  }

  return (
    <div className="guide-page">
      <header className="guide-header">
        <div>
          <span className="rr-eyebrow">Living product context</span>
          <h1>Company Brain</h1>
          <p>{BRAIN_DESCRIPTION} It keeps learning from repository evidence and every reviewed pull request.</p>
        </div>
        <Link className="rr-button" to={repoId ? `/brain/new?repository=${repoId}` : "/brain/new"}>Add context</Link>
      </header>
      <div className="guide-health" data-testid="guide-summary">
        <span className="guide-learning-mark" aria-hidden="true">✦</span>
        <div className="guide-learning-copy"><strong>AI learning is on</strong><span>{repoName ? `Watching ${repoName.owner}/${repoName.name}` : repositories.length ? `Watching ${repositories.length} repositories` : "Connect a repository to begin"}</span></div>
        <dl>
          <div><dt>Confirmed</dt><dd>{confirmed.length}</dd></div>
          <div><dt>Inferred</dt><dd>{inferred.length}</dd></div>
          <div className={attention.length ? "needs-attention" : ""}><dt>Needs attention</dt><dd>{attention.length}</dd></div>
        </dl>
      </div>
      {error ? <InlineAlert tone="alert">{error}</InlineAlert> : null}
      {!repoId && repositories.length > 1 ? (
        <div className="rr-inbox-empty">
          <h2>Choose a codebase</h2>
          <p>Company Brain is saved on the codebase selected above.</p>
          <div className="rr-repo-choices">
            {repositories.map((repo) => (
              <button key={repo._id} type="button" onClick={() => onOpen?.({ repositoryId: repo._id })}>
                {repo.owner}/{repo.name}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="brain-home">
          <nav className="brain-tabs" aria-label="Company Brain">
            {[
              ["rules", "Rules", confirmed.length],
              ["proposals", "Proposals", inferred.length],
              ["sources", "Sources", null],
              ["attention", "Needs attention", attention.length],
            ].map(([id, label, count]) => (
              <button key={id} type="button" aria-current={lens === id ? "page" : undefined} onClick={() => setLens(id)}>
                <span>{label}</span>
                {count ? <span className="brain-count">{count}</span> : null}
              </button>
            ))}
          </nav>
          <section className="brain-panel">
            {lens === "rules" ? (
              <>
                <header>
                  <h2>Rules</h2>
                  <p>Confirmed context the next review can use.</p>
                </header>
                <ul className="guide-list">
                  {confirmed.map((entry) => (
                    <li key={entry._id}>
                      <button type="button" className="guide-row" onClick={() => onOpen?.({ guideId: entry._id, repositoryId: repoId })}>
                        <strong>{ruleLine(entry)}</strong>
                        <span className="guide-meta">{rowMeta(entry)}{repoName ? ` · ${repoName.owner}/${repoName.name}` : ""}</span>
                      </button>
                    </li>
                  ))}
                  {!confirmed.length ? <li className="guide-empty-state"><span className="guide-empty-icon" aria-hidden="true">✦</span><div><h3>Your Brain starts with evidence, not a questionnaire.</h3><p>Review a pull request or connect a repository. RenderRepo will infer product rules, cite where each came from, and ask only when something is ambiguous.</p></div><Link className="rr-button" to="/">Review a pull request</Link></li> : null}
                </ul>
              </>
            ) : null}
            {lens === "proposals" ? (
              <>
                <header>
                  <h2>Proposals</h2>
                  <p>RenderRepo inferred these from evidence. Safe proposals can support reviews as inferred context; confirm one to make it authoritative.</p>
                </header>
                <ul className="guide-list">
                  {inferred.map((item) => (
                    <li key={item._id}>
                      <button type="button" className="guide-row" onClick={() => onOpen?.({ repositoryId: repoId, suggestionId: item._id, section: "suggestions" })}>
                        <span className="guide-row-top"><strong>{ruleLine(item)}</strong><span className="rr-pill">AI inferred</span></span>
                        <span className="guide-meta">{rowMeta(item)}</span>
                      </button>
                    </li>
                  ))}
                  {!inferred.length ? <li><p className="guide-empty">No proposals are waiting.</p></li> : null}
                </ul>
              </>
            ) : null}
            {lens === "sources" ? (
              <>
                <header>
                  <h2>Sources</h2>
                  <p>Where this codebase’s context comes from.</p>
                </header>
                <ul className="brain-sources">
                  <li className="brain-source">
                    <div>
                      <strong>GitHub</strong>
                      <p>{repoName ? `${repoName.owner}/${repoName.name} is connected.` : "Connect a codebase to read its docs and code."}</p>
                      {sourceGroups.length ? <p className="guide-meta">{sourceGroups.map((group) => `${group.name} · ${group.count}`).join("  ")}</p> : null}
                    </div>
                    <span className="brain-source-state">Connected</span>
                  </li>
                  {FUTURE_SOURCES.map((source) => (
                    <li key={source.name} className="brain-source">
                      <div>
                        <strong>{source.name}</strong>
                        <p>Not connected.</p>
                      </div>
                      <span className="brain-source-state">{source.state}</span>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
            {lens === "attention" ? (
              <>
                <header>
                  <h2>Needs attention</h2>
                  <p>Conflicts and outdated context that could make the next review wrong.</p>
                </header>
                <ul className="guide-list">
                  {attention.map((item) => (
                    <li key={item.id}>
                      <button type="button" className="guide-row" onClick={() => onOpen?.({ repositoryId: repoId, section: item.section, suggestionId: item.suggestionId, conflictId: item.conflictId })}>
                        <span className="guide-kicker">{item.state}</span>
                        <strong>{item.title}</strong>
                      </button>
                    </li>
                  ))}
                  {!attention.length ? <li><p className="guide-empty">Nothing needs attention.</p></li> : null}
                </ul>
              </>
            ) : null}
          </section>
        </div>
      )}
    </div>
  );
}
